import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { GenericContentGenerationPopupComponent } from '../generic-content-generation-popup/generic-content-generation-popup.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { PlatformContentCardComponent } from '../../components/platform-content-card/platform-content-card.component';
import { EvidenceAndRecommendationsComponent } from '../../components/evidence-and-recommendations/evidence-and-recommendations.component';
import { ContentCreationArticleTabComponent } from '../content-creation-article-tab/content-creation-article-tab.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';

@Component({
  selector: 'app-generic-content-creation',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    GenericContentGenerationPopupComponent,
    PlatformContentCardComponent,
    EvidenceAndRecommendationsComponent,
    ContentCreationArticleTabComponent,
    // CitationCardComponent
  ],
  templateUrl: './generic-content-creation.component.html',
  styleUrl: './generic-content-creation.component.scss'
})
export class GenericContentCreationComponent implements OnInit, OnDestroy {
  /** Sub-tab: 'article' | 'social' (Social Content Analysis = existing flow) */
  contentCreationSubTab: 'article' | 'social' = 'article';
  // Core data properties
  contentCreationResponse: any = null;
  data: any = null;

  // Loading and error state

  // Content creation data
  evidenceSources: any[] = [];
  recommendations: any[] = [];
  
  // Content generation popup functionality
  showContentGenerationPopup: boolean = false;
  selectedPlatform: any = null;
  selectedRecommendation: any = null;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribe to Content Creation response
    const analysisSub = this.templateCommonService.getContentCreationResponse$().subscribe((response: any) => {
      if (response) {
        this.contentCreationResponse = response;
        this.data = response;
        this.processContentCreationData(response);
        this.cdr.detectChanges();
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub);

    // Load data when component initializes
    this.loadContentCreationData();
  }

  ngOnDestroy(): void {
    console.log('🗑️ Content Creation: Destroying component and clearing memory...');
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.contentCreationResponse = null;
    this.data = null;
    this.evidenceSources = [];
    this.recommendations = [];
    
    console.log('✅ Content Creation: Component destroyed and memory cleared');
  }

  loadContentCreationData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentContentCreationResponse();
    if (currentResponse) {
      console.log('Content Creation: Found existing response, processing immediately');
      this.contentCreationResponse = currentResponse;
      this.data = currentResponse;
      this.processContentCreationData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Content Creation: No data yet, waiting for subscription to receive data');
    }
  }

  onReload(): void {
    this.templateCommonService.triggerTabReload('content-creation', true);
  }

  retryLoad(): void {
    this.onReload();
  }

  hasData(): boolean {
    const hasData = !!(this.data && (
      this.data.brands || 
      this.data.platform_analysis || 
      this.data.content_recommendations ||
      this.data.composite_score ||
      this.data.competitor_analysis ||
      this.data.performance_table
    ));
    
    console.log('🔍 Content Creation hasData check:', {
      hasData: hasData,
      data: this.data,
      dataKeys: this.data ? Object.keys(this.data) : 'no data'
    });
    
    return hasData;
  }

  private processContentCreationData(data: any): void {
    console.log('Content Creation: Processing data:', data);
    
    // Handle both direct response and structured_data response
    const responseData = data.structured_data || data;
    this.data = responseData;
    
    // Extract evidence sources from multiple possible locations
    let citations: any[] = [];
    
    // Try structured_data.evidence_sources first (new format)
    if (data.structured_data?.evidence_sources && Array.isArray(data.structured_data.evidence_sources)) {
      citations = data.structured_data.evidence_sources;
    }
    // Try responseData.structured_data.evidence_sources
    else if (responseData.structured_data?.evidence_sources && Array.isArray(responseData.structured_data.evidence_sources)) {
      citations = responseData.structured_data.evidence_sources;
    }
    // Try root level evidence_sources
    else if (responseData.evidence_sources && Array.isArray(responseData.evidence_sources)) {
      citations = responseData.evidence_sources;
    }
    // Try references.sources
    else if (responseData.references?.sources && Array.isArray(responseData.references.sources)) {
      citations = responseData.references.sources;
    }
    // Try root level citations
    else if (responseData.citations && Array.isArray(responseData.citations)) {
      citations = responseData.citations;
    }
    // Try context.citations
    else if (responseData.context?.citations && Array.isArray(responseData.context.citations)) {
      citations = responseData.context.citations;
    }
    // Try structured_data.context.citations (when data is at root)
    else if (data.structured_data?.context?.citations && Array.isArray(data.structured_data.context.citations)) {
      citations = data.structured_data.context.citations;
    }
    // Try per-brand citations and aggregate them (when competitors are present)
    else if (responseData.brands && Array.isArray(responseData.brands)) {
      const allCitations: any[] = [];
      responseData.brands.forEach((brand: any) => {
        if (brand.citations && Array.isArray(brand.citations)) {
          allCitations.push(...brand.citations);
        }
        if (brand.references?.sources && Array.isArray(brand.references.sources)) {
          allCitations.push(...brand.references.sources);
        }
        if (brand.context?.citations && Array.isArray(brand.context.citations)) {
          allCitations.push(...brand.context.citations);
        }
      });
      // Remove duplicates based on URL
      const uniqueCitations = allCitations.filter((citation, index, self) =>
        index === self.findIndex(c => c.url === citation.url)
      );
      citations = uniqueCitations;
    }
    
    this.evidenceSources = citations;
    
    console.log('Content Creation: Processed data:', this.data);
    console.log('Content Creation: Evidence sources:', this.evidenceSources);
    
    this.cdr.markForCheck();
  }

  getOverallContentScore(): number {
    if (!this.data) return 0;
    // API returns composite_score.value (e.g. from /generic/history)
    // if (this.data.composite_score?.value != null) {
    //   return Number(this.data.composite_score.value);
    // }
    // For platform-based analysis, calculate score from platform data
    if (this.data.platform_analysis) {
      const platforms = this.getAllPlatforms();
      if (platforms.length === 0) return 0;

      // Calculate score based on platform status
      let totalScore = 0;
      platforms.forEach(platform => {
        switch (platform.current_status?.toLowerCase()) {
          case 'excellent': totalScore += 90; break;
          case 'good foundation': totalScore += 75; break;
          case 'functional but generic': totalScore += 60; break;
          case 'basic presence': totalScore += 45; break;
          case 'limited engagement': totalScore += 30; break;
          case 'underutilized': totalScore += 25; break;
          case 'needs review': totalScore += 20; break;
          default: totalScore += 50;
        }
      });
      return Math.round(totalScore / platforms.length);
    }

    // Fallback to brand-based scoring
    if (!this.data.brands || this.data.brands.length === 0) {
      return 0;
    }
    const scores = this.data.brands.map((brand: any) => brand.content_status?.content_score || 0);
    return Math.round(scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length);
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    return 'score-poor';
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    return '#dc3545';
  }

  getQualityColor(score: number): string {
    if (score >= 80) return 'primary';
    if (score >= 60) return 'accent';
    return 'warn';
  }

  getCredibilityColor(score: number): string {
    if (score >= 8) return '#28a745';
    if (score >= 6) return '#ffc107';
    return '#dc3545';
  }

  // Platform-based analysis methods
  getPlatformCategories(): string[] {
    if (!this.data?.platform_analysis) {
      return [];
    }
    return Object.keys(this.data.platform_analysis);
  }

  getPlatformsByCategory(category: string): any[] {
    if (!this.data?.platform_analysis || !this.data.platform_analysis[category]) {
      return [];
    }
    return this.data.platform_analysis[category];
  }

  getAllPlatforms(): any[] {
    if (!this.data?.platform_analysis) {
      return [];
    }
    const allPlatforms: any[] = [];
    Object.values(this.data.platform_analysis).forEach((category: any) => {
      if (Array.isArray(category)) {
        allPlatforms.push(...category);
      }
    });
    return allPlatforms;
  }

  // Content generation popup functionality
  generateContent(platform: any): void {
    this.selectedPlatform = platform;
    this.showContentGenerationPopup = true;
  }

  generateContentFromRecommendation(recommendation: any): void {
    this.selectedRecommendation = recommendation;
    this.showContentGenerationPopup = true;
  }

  closeContentGenerationPopup(): void {
    this.showContentGenerationPopup = false;
    this.selectedPlatform = null;
    this.selectedRecommendation = null;
  }

  getRecommendationForPopup(): any {
    const brandName = this.data?.brand || 'Brand';
    
    if (this.selectedPlatform) {
      return {
        brand: brandName,
        category: this.selectedPlatform.sub_platform,
        issue: this.selectedPlatform.specific_issue,
        priority: this.selectedPlatform.priority,
        current_score: 0
      };
    } else if (this.selectedRecommendation) {
      return {
        brand: brandName,
        category: this.selectedRecommendation.content_type,
        issue: this.selectedRecommendation.title,
        priority: 'medium',
        current_score: this.selectedRecommendation.impact_score || 0
      };
    }
    return null;
  }

  // Transform content creation recommendations to evidence-and-recommendations format
  getTransformedRecommendations(): any[] {
    if (!this.data?.content_recommendations) return [];

    return this.data.content_recommendations.map((rec: any) => ({
      category: rec.content_type || 'Content Creation',
      issue: rec.title || 'Content improvement opportunity',
      recommendation: `Create ${rec.piece_type} content focusing on: ${rec.keywords?.slice(0, 3).join(', ')}`,
      priority: rec.impact_score >= 85 ? 'High' : rec.impact_score >= 70 ? 'Medium' : 'Low',
      expected_impact: `Impact Score: ${rec.impact_score}/100 | Effort: ${rec.estimated_effort}`
    }));
  }

  // Get evidence sources (if any)
  getEvidenceSources(): any[] {
    return this.evidenceSources || [];
  }

  // Get citations for citation-card component (same as Overview)
  getCitations(): any[] {
    if (!this.contentCreationResponse) return [];
    
    // Get citations from multiple possible locations
    const citations = this.contentCreationResponse.structured_data?.evidence_sources ||
                     this.contentCreationResponse.structured_data?.context?.citations ||
                     this.contentCreationResponse.evidence_sources ||
                     this.contentCreationResponse.context?.citations ||
                     this.evidenceSources ||
                     [];
    
    return citations.length > 0 ? this.convertToCitations(citations) : [];
  }

  // Convert evidence sources to citation format
  private convertToCitations(sources: any[]): any[] {
    return sources.map((source: any) => ({
      title: source.title || source.source || '',
      url: source.url || '',
      snippet: source.snippet || source.description || '',
      source: source.source || source.domain || 'Web search',
      domain: source.domain || source.source || '',
      confidence: source.confidence || source.confidence_score || 0.8,
      type: source.type || 'web'
    }));
  }
}


